import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { autoMessagesApi, type CreateAutoMessageBody, type UpdateAutoMessageBody } from '@/api/auto-messages.api';

export const autoMessageKeys = {
  all: ['auto-messages'] as const,
  lists: () => [...autoMessageKeys.all, 'list'] as const,
  detail: (id: string) => [...autoMessageKeys.all, 'detail', id] as const,
};

export function useAutoMessages() {
  return useQuery({
    queryKey: autoMessageKeys.lists(),
    queryFn: () => autoMessagesApi.list(),
  });
}

export function useAutoMessage(id: string | undefined) {
  return useQuery({
    queryKey: autoMessageKeys.detail(id || ''),
    queryFn: () => autoMessagesApi.get(id as string),
    enabled: !!id,
  });
}

export function useCreateAutoMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAutoMessageBody) => autoMessagesApi.create(body),
    onSuccess: () => {
      toast.success('Mensaje automático creado');
      qc.invalidateQueries({ queryKey: autoMessageKeys.all });
    },
    onError: (err: Error) => toast.error(err.message || 'Error al crear'),
  });
}

export function useUpdateAutoMessage(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateAutoMessageBody) => autoMessagesApi.update(id, body),
    onSuccess: () => {
      toast.success('Mensaje automático actualizado');
      qc.invalidateQueries({ queryKey: autoMessageKeys.all });
    },
    onError: (err: Error) => toast.error(err.message || 'Error al actualizar'),
  });
}

export function useDeleteAutoMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => autoMessagesApi.remove(id),
    onSuccess: () => {
      toast.success('Mensaje automático eliminado');
      qc.invalidateQueries({ queryKey: autoMessageKeys.all });
    },
    onError: (err: Error) => toast.error(err.message || 'Error al eliminar'),
  });
}
